
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Users, BookOpen, DollarSign, Calendar, Clock, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import TeacherLayout from "@/components/TeacherLayout";
import { useToast } from "@/hooks/use-toast";
import { createApiUrl } from "@/lib/api";

interface DashboardStats {
  teacher_name: string;
  active_classes: number;
  total_students: number;
  total_fees_collected: number;
  pending_fees: number;
}

interface ApiClass {
  _id: string;
  name: string;
  description: string;
  subject: string;
  grade: string;
  schedule: Record<string, { start_time: string; end_time: string }>;
  status: string;
  max_students: number;
  fees: number;
}

const TeacherDashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [classes, setClasses] = useState<ApiClass[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const teacherData = localStorage.getItem('teacherData');
        const accessToken = localStorage.getItem('accessToken');
        
        if (!teacherData || !accessToken) {
          navigate('/teacher/login');
          return;
        }

        const dashboardResponse = await fetch(createApiUrl('/teacher/dashboard'), {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const classesResponse = await fetch(createApiUrl('/teacher/classes'), {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        if (dashboardResponse.ok && classesResponse.ok) {
          const dashboardData = await dashboardResponse.json();
          const classesData = await classesResponse.json();
          
          console.log('Dashboard data:', dashboardData);
          console.log('Classes data:', classesData);
          
          setStats(dashboardData);
          setClasses(classesData.classes || []);
        } else if (dashboardResponse.status === 401 || classesResponse.status === 401) {
          localStorage.removeItem('teacherData');
          localStorage.removeItem('accessToken');
          toast({
            title: "Session Expired",
            description: "Please login again",
            variant: "destructive"
          });
          navigate('/teacher/login');
        } else {
          throw new Error('Failed to fetch dashboard data');
        }
      } catch (error) {
        console.error('Dashboard fetch error:', error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive"
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [navigate, toast]);

  const getUpcomingClasses = () => {
    const today = new Date();
    const currentDay = today.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();

    return classes
      .filter(cls => cls.status === 'active')
      .flatMap(cls => 
        Object.entries(cls.schedule).map(([day, times]) => ({
          id: `${cls._id}-${day}`,
          name: `${cls.grade} - ${cls.subject}`,
          time: `${day.charAt(0).toUpperCase() + day.slice(1)}, ${times.start_time}`,
          students: cls.max_students,
          classId: cls._id
        }))
      )
      .slice(0, 3);
  };

  const getRecentClasses = () => {
    return classes
      .filter(cls => cls.status === 'active')
      .map(cls => ({
        id: cls._id,
        name: `${cls.grade} - ${cls.subject}`,
        subject: cls.name,
        lastClass: 'Recent',
        students: cls.max_students
      }))
      .slice(0, 4);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm sm:text-base">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-gray-600 mb-4 text-sm sm:text-base">Failed to load dashboard data</p>
          <Button onClick={() => window.location.reload()} size="sm">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const upcomingClasses = getUpcomingClasses();
  const recentClasses = getRecentClasses();

  return (
    <TeacherLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Welcome Header */}
        <div className="bg-white rounded-lg p-4 sm:p-6 shadow-sm">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
            Welcome back, {stats.teacher_name}! 👋
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">Here's what's happening with your classes today</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm">Active Classes</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.active_classes}</p>
                </div>
                <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-blue-200 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm">Total Students</p>
                  <p className="text-lg sm:text-2xl font-bold">{stats.total_students}</p>
                </div>
                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-green-200 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-purple-100 text-xs sm:text-sm">Fees Collected</p>
                  <p className="text-lg sm:text-2xl font-bold truncate">₹{stats.total_fees_collected.toLocaleString()}</p>
                </div>
                <DollarSign className="w-6 h-6 sm:w-8 sm:h-8 text-purple-200 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-orange-100 text-xs sm:text-sm">Pending Fees</p>
                  <p className="text-lg sm:text-2xl font-bold truncate">₹{stats.pending_fees.toLocaleString()}</p>
                </div>
                <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-orange-200 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              <Button 
                variant="outline" 
                className="h-auto p-3 sm:p-4 flex-col gap-1 sm:gap-2 text-xs sm:text-sm"
                onClick={() => navigate('/teacher/students')}
              >
                <Users className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Manage Students</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-3 sm:p-4 flex-col gap-1 sm:gap-2 text-xs sm:text-sm"
                onClick={() => navigate('/teacher/classes')}
              >
                <BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>My Classes</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-3 sm:p-4 flex-col gap-1 sm:gap-2 text-xs sm:text-sm"
                onClick={() => navigate('/teacher/fees')}
              >
                <DollarSign className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Collect Fees</span>
              </Button>
              <Button 
                variant="outline" 
                className="h-auto p-3 sm:p-4 flex-col gap-1 sm:gap-2 text-xs sm:text-sm"
                onClick={() => navigate('/teacher/attendance')}
              >
                <Calendar className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Mark Attendance</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Classes */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              Upcoming Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcomingClasses.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {upcomingClasses.map((classItem) => (
                  <div key={classItem.id} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-800 text-sm sm:text-base truncate">{classItem.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{classItem.time}</p>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <Badge variant="secondary" className="text-xs">Max {classItem.students}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <Calendar className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm sm:text-base">No upcoming classes scheduled</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Classes */}
        <Card>
          <CardHeader className="pb-3 sm:pb-4">
            <CardTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-indigo-600" />
              Your Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentClasses.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {recentClasses.map((classItem) => (
                  <div key={classItem.id} className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-gray-800 text-sm sm:text-base truncate">{classItem.name}</h4>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">{classItem.subject}</p>
                    </div>
                    <div className="text-right ml-2 flex-shrink-0">
                      <Badge variant="outline" className="text-xs">Max {classItem.students}</Badge>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 sm:py-8">
                <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 mb-3 sm:mb-4 text-sm sm:text-base">No classes created yet</p>
                <Button 
                  onClick={() => navigate('/teacher/classes')}
                  className="bg-indigo-600 hover:bg-indigo-700 text-sm"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  Create Your First Class
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TeacherLayout>
  );
};

export default TeacherDashboard;
